import React from 'react';
import { cn } from '~/lib/utils';
import styles from './DataTable.module.css';
import type { ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface DataTableColumn {
  /** The column header. Plain text — a header with markup in it is a data cell. */
  header: string;
  /**
   * Fixed px, or `'fill'` for the column that absorbs the remainder. The *Rich answer* table is `70 / 60 / 'fill'`.
   * More than one `'fill'` is legal and splits the remainder evenly, but the design only ever
   * fills the last column.
   */
  width: number | 'fill';
}

export interface DataTableProps {
  columns: DataTableColumn[];
  /** Row-major cell text. A short row is padded with empty cells rather than collapsing the grid. */
  rows: string[][];
  /**
   * Names the table for assistive tech. Not rendered — the artboard draws no caption, and the
   * sentence introducing the table is a sibling `AssistantProse` block, not part of this component.
   */
  label?: string;
  theme?: ChatTheme;
  className?: string;
}

/**
 * The three-column bordered table of a structured assistant answer — "a heading, a table
 * and numbered steps. prose was only ever paragraphs."
 *
 * A real `<table>` with `<th scope="col">`, not the flex frames Paper draws. Paper has no table
 * primitive, so its export is three rows of boxes; shipping that would mean a reader who lands on
 * "505px" has no way to learn it is the height for 1000 chars. The visual result is identical
 * because `table-layout: fixed` plus a `<colgroup>` reproduces the 70/60/fill split exactly.
 */
export function DataTable({
  columns,
  rows,
  label,
  theme: themeProp,
  className,
}: DataTableProps) {
  const theme = useV15Theme(themeProp);
  return (
    <div className={cn(chatThemeClass(theme), styles.root, className)}>
      <table className={styles.table} aria-label={label}>
        <colgroup>
          {columns.map((column, index) => (
            // The width is the only runtime value in the component, so it is the only thing passed
            // inline — and it goes in as a custom property the stylesheet reads, never as a direct
            // `width`, so the column mechanism stays restylable from the CSS. `'fill'` becomes
            // `auto`, which under `table-layout: fixed` means "whatever the fixed columns leave" —
            // the flex-grow of the table world.
            <col
              key={index}
              style={
                {
                  '--cv15-col-w': column.width === 'fill' ? 'auto' : `${column.width}px`,
                } as React.CSSProperties
              }
            />
          ))}
        </colgroup>
        <thead>
          <tr>
            {columns.map((column, index) => (
              // Index, not `column.header`: `header` is typed as a free string with no uniqueness
              // constraint, so two columns sharing a label — or a blank leading label column —
              // would collide into one React key. Position is the only identity a column has here.
              <th key={index} scope="col" className={cn(styles.cell, styles.head)}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            // Index keys throughout: this is rendered answer content, immutable once streamed —
            // rows never reorder and no cell holds state.
            <tr key={rowIndex}>
              {columns.map((_column, cellIndex) => (
                // Index for the same reason as the header cells above, and for the reason the row
                // itself is index-keyed: a rendered answer's columns are fixed by position and
                // never reorder, while `header` is a free string that may repeat or be empty.
                <td key={cellIndex} className={cn(styles.cell, styles.data)}>
                  {row[cellIndex] ?? ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
